import { useState } from "react";
import { Link, useLocation } from "wouter";
import { useAuth } from "../../auth/AuthContext";
import { canManageFleet } from "../../auth/roleView";
import { useProjects } from "../../api/queries";
import type { Project } from "../../api/types";
import { EmptyState } from "../../components/AsyncStates";
import { Button } from "../../components/Button";
import { DataTable, type DataTableColumn } from "../../components/DataTable";
import { PageHeader } from "../../components/PageHeader";
import { PaginatorControls, usePaginator } from "../../components/Paginator";
import { QueryStates } from "../../components/QueryStates";
import { CorpusStatePillCell } from "./CorpusStatePillCell";
import { CreateProjectModal } from "./CreateProjectModal";

const COLUMNS: DataTableColumn<Project>[] = [
  {
    key: "name",
    header: "Name",
    cell: (project) => (
      // stopPropagation: the row itself is also a click target (DataTable's
      // onRowClick); without this, activating the link would navigate twice.
      <Link className="data-table__primary-link" href={`/projects/${project.id}`} onClick={(e) => e.stopPropagation()}>
        {project.name}
      </Link>
    ),
    className: "data-table__primary",
  },
  {
    key: "corpus",
    header: "Corpus",
    cell: (project) => <CorpusStatePillCell projectId={project.id} />,
  },
  {
    key: "createdBy",
    header: "Created by",
    cell: (project) => project.createdBy || "—",
    className: "data-table__secondary",
  },
  {
    key: "updatedAt",
    header: "Updated",
    cell: (project) => new Date(project.updatedAt).toLocaleString(),
    className: "data-table__secondary",
  },
];

/**
 * Develop's landing page (IA slice 3, ui-information-architecture.md §3): projects group
 * the services a team deploys together, and this is the entry point into the Develop
 * arc's other on-ramps — Gallery (scaffolds), Adopt (bring existing infra), and Sessions
 * (the fleet-wide hosted-workbench admin view, `/agent-sessions` — a project's own Sessions
 * tab only lists ITS develop sessions and lacks the Stop/Delete verbs this one has) — which
 * stay their own routes (the nav's Develop section already lights up for all three, see
 * NavSidebar.tsx) but get a direct pointer from here too, since a Founder or Migrator
 * landing on Develop needs to find them without already knowing the nav taxonomy. Before
 * the "Sessions" link was added here, `/agent-sessions` (and its `/bonnie` cross-link, see
 * AgentSessionsPage/BonnieSessionsPage) had no link into it from anywhere in the app —
 * same orphaned-canonical-page gap as Fleet's `/environments`/`/routes`/`/instances`.
 * Mirrors the real, merged backend contract (#63 projectsvc) exactly.
 */
export function ProjectsListPage() {
  const { session } = useAuth();
  const [, navigate] = useLocation();
  const paginator = usePaginator();
  const { data, isPending, isError, error, refetch, isFetching } = useProjects({
    cursor: paginator.cursor,
  });
  const [createOpen, setCreateOpen] = useState(false);

  // Mirrors the server's authz matrix (project:create is granted to
  // operator/admin only, server/internal/authz/model.go) — this only hides
  // the control; the server enforces the real decision on every request
  // regardless of what this check says (S-8).
  const canCreate = canManageFleet(session?.role);

  const newProjectButton = (
    <Button
      variant="primary"
      // No fleet-id resolution to wait on before opening this modal: ADR-0035 M1 PR-D
      // deleted FleetContext.tsx — tenant comes solely from the session now, which is
      // already resolved by the time this page renders (same as gallery's "Deploy this
      // stack", GalleryPage.tsx).
      onClick={() => setCreateOpen(true)}
    >
      New project
    </Button>
  );

  const headerActions = (
    <>
      <Link to="/gallery" className="button button--secondary">
        Gallery
      </Link>
      <Link to="/import" className="button button--secondary">
        Adopt existing infra
      </Link>
      <Link to="/agent-sessions" className="button button--secondary">
        Sessions
      </Link>
      {canCreate && newProjectButton}
    </>
  );

  return (
    <>
      <PageHeader
        title="Develop"
        description="Group the services your team builds and deploys together — the starting point for project → service → deploy. Start from a scaffold in Gallery, or adopt infra you're already running."
        actions={headerActions}
      />

      <QueryStates
        isLoading={isPending}
        loadingLabel="Loading projects…"
        isError={isError}
        error={error}
        onRetry={refetch}
        errorDescription={
          error instanceof Error ? error.message : "Couldn't load projects. Check your connection and try again."
        }
        isEmpty={Boolean(data && data.items.length === 0)}
        empty={
          <EmptyState
            title="No projects yet"
            description="Create one to start deploying services to this fleet."
            action={canCreate ? newProjectButton : undefined}
          />
        }
      >
        {data && (
          <>
            <DataTable
              columns={COLUMNS}
              rows={data.items}
              rowKey={(project) => project.id}
              onRowClick={(project) => navigate(`/projects/${project.id}`)}
              ariaBusy={isFetching}
            />
            <PaginatorControls
              itemCount={data.items.length}
              nextCursor={data.nextCursor}
              paginator={paginator}
              noun="projects"
            />
          </>
        )}
      </QueryStates>

      {canCreate && (
        <CreateProjectModal open={createOpen} onClose={() => setCreateOpen(false)} />
      )}
    </>
  );
}
